import type { SettingsV1 } from '../types/settings-v1.schema';

/** Settings with every field set, the ones the schema leaves optional for games stored before them included. */
export type CompleteSettings = SettingsV1 & Required<Pick<SettingsV1, 'swappedTilePoints'>>;

/** Freezes `value` and every object inside it, so a shared default can't be changed by whoever reads it. */
const deepFreeze = <T extends object>(value: T): Readonly<T> => {
  for (const inner of Object.values(value)) {
    if (inner !== null && typeof inner === 'object') deepFreeze(inner);
  }
  return Object.freeze(value);
};

/**
 * The game's default rules, and the only place a default is written (WH-260). A new app game is created with them under
 * the newest `RULES_VERSION`, and the Game Simulator opens on them less `v`.
 *
 * - Swapping is WH-178's: swap once, then play, up to 4 tiles, each drawn tile held to 1 point.
 * - The bag is a hundred single letters: clusters are an experiment, dealt in only from the Scenario Lab.
 * - Diagonals are off: the rule costs about a third of a game (`docs/game-rules.md` section 5).
 * - Premium squares are drawn per game: 8 double-word and 4 triple-word, at most 2 a 3 × 3 block, mirrored.
 * - Hot letters are off (WH-233 turns them on). Their rule is WH-197's: two a turn, new every turn, picked at random
 *   and spread, one a word, 3 points each. The server plays only `lifetime: 'per-turn'` and `topUp: 'every-turn'`.
 *
 * A game stores the settings it was created with, so changing a value here changes only games created after it and
 * needs no new rules version.
 */
export const DEFAULT_SETTINGS: Readonly<CompleteSettings> = deepFreeze<CompleteSettings>({
  v: 1,
  shape: 'classic',
  diagonals: false,
  swapMode: 'then-play',
  swapLimit: 4,
  swappedTilePoints: 1,
  composition: { single: 100, double: 0, triple: 0, size: 100 },
  hotLetters: false,
  hotLetterSettings: {
    count: 2,
    lifetime: 'per-turn',
    selection: 'distinct-words',
    ranking: 'spread',
    bonus: { kind: 'points', value: 3 },
    topUp: { kind: 'every-turn', cap: 2, period: 3, earnBonus: 0 },
  },
  randomStart: false,
  premiumSquares: true,
  randomPremiums: true,
  premiumBlock: 3,
  premiumsPerBlock: 2,
  premiumSymmetry: 'mirror',
  doubleWordCount: 8,
  tripleWordCount: 4,
});

/**
 * Whether `dr` and `ur` runs are read as words, unless a move or a board says otherwise: the default's `diagonals`.
 * With it `false`, a move that meets the board only at a corner does not connect, and a cluster tile may not be turned
 * onto a diagonal. Diagonal play is an experiment; see `docs/game-rules.md`.
 */
export const DIAGONAL_WORDS: boolean = DEFAULT_SETTINGS.diagonals;
